import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Client } from '../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { Gruppe } from '../../../../../projects/kiene-core/src/lib/components/gruppen/classes/gruppe';
import { GruppenService } from '../../../../../projects/kiene-core/src/lib/components/gruppen/gruppen.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { KieneAutocompleteComponent } from '../../../components/kiene-autocomplete/kiene-autocomplete.component';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import {
    Antibiogramm,
    AntibiogrammOrder,
    AntibiogrammOrderResult,
    AntibiogrammTierart,
    HeaderRecord,
    Infektionsart,
    PatientRecord,
    ResTest,
    ResultRecord,
    TestOrderRecord,
    TestOrderResultRecord,
    Verdacht,
} from '../antibiogramm';

import { AntibiogrammService } from '../antibiogramm.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';

@Component({
    selector: 'kiene-antibiogramm-details',
    templateUrl: './antibiogramm-details.component.html',
    styleUrls: ['./antibiogramm-details.component.scss'],
})
export class AntibiogrammDetailsComponent implements OnInit {
    antibiogramm: Antibiogramm = new Antibiogramm();
    currentClient: Client;
    currentIsolat: ResTest = new ResTest();
    resTestList: ResTest[] = [];

    currentGruppe: Gruppe = new Gruppe();

    isLoading = false;

    currentId: string;

    testDescription: AntibiogrammOrder;

    gruppe: Gruppe;

    testResult: AntibiogrammOrderResult;

    tierarten: Observable<AntibiogrammTierart[]>;
    tierartCtrl = new FormControl();

    probenentnahmeCtrl = new FormControl();

    currentVerdacht: Verdacht;
    verdachte: Verdacht[] = [];

    filteredInfektionsarten: Observable<Infektionsart[]>;
    infektionCtrl: UntypedFormControl = new UntypedFormControl();
    infektionsarten: Infektionsart[] = [
        { bezeichnung: 'Genitaltraktinfektion', kuerzel: 'G' },
        { bezeichnung: 'Mastitis', kuerzel: 'M' },
        { bezeichnung: 'Atemwegsinfektion', kuerzel: 'RTI' },
        { bezeichnung: 'Hautinfektion', kuerzel: 'S' },
        { bezeichnung: 'Weichteilinfektion', kuerzel: 'ST' },
        { bezeichnung: 'Urogenitalinfektion', kuerzel: 'UG' },
        { bezeichnung: 'Harnwegsinfektion', kuerzel: 'UTI' },
        { bezeichnung: 'Wundinfektion + Abszess', kuerzel: 'W+A' },
    ];

    @ViewChild('inputGruppeAutocomplete', { read: ElementRef })
    inputGruppeAutocomplete: ElementRef;

    @ViewChild('verdachtAutocomplete')
    verdachtAutocomplete: KieneAutocompleteComponent;

    @ViewChild('gruppeAutocomplete')
    gruppeAutocomplete: KieneAutocompleteComponent;
    currentClientParams: HttpParams;
    tierartenList: AntibiogrammTierart[] = [];

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly route: ActivatedRoute,
        private readonly messageService: MessageService,
        public dialog: MatDialog,
        private readonly gruppenService: GruppenService,
        private readonly router: Router,
        private readonly service: AntibiogrammService,
        private readonly localStorageService: LocalStorageService,
    ) {
        this.currentId = this.route.snapshot.paramMap.get('id');
    }

    ngOnInit() {
        this.currentIsolat.isolatenNummer = -1;

        this.loadAntibiogramm();
        this.ladeTierarten();
        this.ladeVerdachte();

        this.autocompleteInfektionsarten();

        this.probenentnahmeCtrl.valueChanges.subscribe({
            next: value => {
                this.antibiogramm.probenentnahme = value;
            },
        });
    }

    hasPermission(permissionId: number): boolean {
        return this.localStorageService.currentUserHasPermission(permissionId);
    }

    loadAntibiogramm() {
        this.service.getAntibiogramm(this.currentId).subscribe({
            next: result => {
                this.antibiogramm = result;

                const gruppe = new Gruppe();
                gruppe.gruppe_id = this.antibiogramm.gruppe_id;
                gruppe.bezeichnung = this.antibiogramm.gruppe;
                this.currentGruppe = gruppe;

                const kunde = new Client();
                kunde.kunde_id = this.antibiogramm.kunde_id;
                kunde.bezeichnung = this.antibiogramm.kunde;
                kunde.kunde_nr_intern = this.antibiogramm.kunde_nr_intern;
                this.currentClient = kunde;

                this.probenentnahmeCtrl.setValue(this.antibiogramm.probenentnahme);

                this.testDescription = this.antibiogramm.content;
                this.initRestTestList();
                this.testResult = this.antibiogramm.ergebnis;

                if (this.antibiogramm?.gruppe_id) {
                    this.loadCurrentGruppe(this.antibiogramm?.gruppe_id);
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler', err);
            },
        });
    }

    openPdf(antibiogramm: Antibiogramm) {
        const pdfId = antibiogramm.pdf_id;
        if (pdfId) {
            this.service.showPdf(antibiogramm);
        }
    }

    loadCurrentGruppe(groupId: number) {
        this.gruppenService.getGruppePraxissoftware(groupId).subscribe({
            next: response => {
                this.currentGruppe = <Gruppe>response;
            },
            error: err => {
                this.messageService.errorMessage('Fehler', err);
            },
        });
    }

    ladeVerdachte() {
        this.service.ladeVerdachte().subscribe({
            next: response => {
                this.verdachte = response.records;
            },
            error: err => {
                this.messageService.errorMessage('Fehler', err);
            },
        });
    }

    autocompleteInfektionsarten() {
        this.infektionCtrl = new UntypedFormControl();
        this.filteredInfektionsarten = this.infektionCtrl.valueChanges.pipe(
            startWith(''),
            map(infektionsart =>
                infektionsart ? this.searchInfektionsarten(infektionsart) : this.infektionsarten.slice(),
            ),
        );
    }

    searchInfektionsarten(value: any) {
        if (value instanceof Infektionsart) {
            return this.infektionsarten.filter(
                infekt => infekt.bezeichnung?.toLowerCase()?.indexOf(value.bezeichnung?.toLowerCase()) === 0,
            );
        } else if (typeof value === 'string') {
            return this.infektionsarten.filter(
                infekt => infekt.bezeichnung?.toLowerCase()?.indexOf(value?.toLowerCase()) === 0,
            );
        }
    }

    displayInfektionsart(infektionsart?: Infektionsart) {
        return infektionsart ? infektionsart.bezeichnung + ' - ' + infektionsart.kuerzel : undefined;
    }

    resendAntibiogrammToMCN6() {
        const anti = new Antibiogramm();
        anti.status = 0;
        anti.antibiogramm_id = this.antibiogramm.antibiogramm_id;
        this.service.updateAntibiogramm(anti).subscribe({
            next: () => {
                this.messageService.infoMessage(
                    'Antibiogramm ' + this.antibiogramm.tagebuch_nr + ' wurde nochmals an MCN6 übertragen!',
                );
            },
            error: error => {
                this.messageService.errorMessage(
                    'Fehler: Antibiogramm ' + this.antibiogramm.tagebuch_nr + ' konnte nicht an MCN6 übertragen weden!',
                    error,
                );
            },
        });
    }

    saveAntibiogrammOrder(status: number) {
        const headerRecord: HeaderRecord = this.createHeaderRecord();
        const patientRecord: PatientRecord = this.createPatientRecord();
        const testOrderResultRecords: TestOrderResultRecord[] = this.createTestOrderResultRecords();

        const antibiogrammOrder = new AntibiogrammOrder();
        antibiogrammOrder.headerRecord = headerRecord;
        antibiogrammOrder.patientRecord = patientRecord;
        antibiogrammOrder.testOrderResultRecords = testOrderResultRecords;

        const antibio: Antibiogramm = new Antibiogramm();
        antibio.tagebuch_nr = this.antibiogramm.tagebuch_nr;
        antibio.gruppe_id = this.currentGruppe.gruppe_id;
        antibio.gruppe = this.currentGruppe.bezeichnung;
        antibio.status = status;
        antibio.content = JSON.stringify(antibiogrammOrder);
        antibio.eingang = this.antibiogramm.eingang;
        antibio.probenentnahme = this.antibiogramm.probenentnahme;

        if (this.antibiogramm.antibiogramm_id) {
            antibio.antibiogramm_id = this.antibiogramm.antibiogramm_id;
            this.api.post(this.apiBaseUrl + 'antibiogramm/update_by_id.php', antibio, null).subscribe({
                next: result => {
                    this.antibiogramm = result;
                    this.testDescription = this.antibiogramm.content;
                    this.probenentnahmeCtrl.setValue(this.antibiogramm.probenentnahme, { emitEvent: false });
                    this.resTestList = [];
                    this.currentClient = new Client();
                    this.initRestTestList();
                    const snackbarRef = this.messageService.infoMessageAction(
                        'Antibiogramm ' + result.tagebuch_nr + ' wurde erfolgreich gespeichert!',
                        'Zu Befund springen',
                    );
                    snackbarRef.subscribe(() => {
                        if (result?.befund_id) {
                            this.router.navigate(['/labor/befundmitteilungen/' + result.befund_id]).then();
                        } else {
                            this.router.navigate(['/labor/antibiogramme']).then();
                        }
                    });
                },
                error: error => {
                    this.messageService.errorMessage(
                        'Fehler: Antibiogramm ' + antibio.tagebuch_nr + ' konnte nicht gespeichert werden!',
                        error,
                    );
                },
            });
        } else {
            this.api.post(this.apiBaseUrl + 'antibiogramm/create.php', antibio).subscribe({
                next: result => {
                    this.antibiogramm = new Antibiogramm();
                    this.resTestList = [];
                    this.currentClient = new Client();
                    this.messageService.infoMessage(
                        'Antibiogramm ' + result.tagebuch_nr + ' wurde erfolgreich gespeichert!',
                    );
                    if (result?.befund_id) {
                        this.router.navigate(['/labor/befundmitteilungen/' + result.befund_id]).then();
                    } else {
                        this.router.navigate(['/labor/antibiogramme']).then();
                    }
                },
                error: error => {
                    this.messageService.errorMessage(
                        'Fehler: Antibiogramm ' + antibio.tagebuch_nr + ' konnte nicht gespeichert werden!',
                        error,
                    );
                },
            });
        }
    }

    createTestOrderResultRecords(): TestOrderResultRecord[] {
        const torr: TestOrderResultRecord[] = [];

        for (let i = 0; i < this.resTestList.length; i++) {
            const test = this.resTestList[i];
            const testOrderResultRecord = new TestOrderResultRecord();
            const testOrderRecord = new TestOrderRecord();
            const resultRecord = new ResultRecord();

            testOrderRecord.sequenceNumber = 1;
            testOrderRecord.materialNummer = this.antibiogramm.tagebuch_nr;
            testOrderRecord.isolatenNummer = test.isolatenNummer;
            testOrderRecord.isoKommentar = test.kommentar;
            testOrderRecord.einsenderKuerzel = this.tierartKuerzel(test.tierart);
            testOrderRecord.materialartenKuerzel = this.infektionsartKuerzel(test.infektionsart);
            testOrderRecord.kennzeichnung = test.kennzeichnung;

            testOrderResultRecord.testOrderRecord = testOrderRecord;

            resultRecord.sequenceNumber = test.isolatenNummer;
            resultRecord.testart = test.testart;
            resultRecord.verdacht = test.verdacht;

            testOrderResultRecord.resultRecord = resultRecord;

            torr.push(testOrderResultRecord);
        }

        return torr;
    }

    createPatientRecord(): PatientRecord {
        const pr = new PatientRecord();
        pr.patientenId = this.currentGruppe.gruppe_nr_intern
            ? this.currentGruppe.gruppe_nr_intern.toString()
            : this.currentGruppe.kunde_nr_intern?.toString();
        pr.patientenName = this.getPatientenNamen();
        pr.sequenceNumber = 1;

        return pr;
    }

    getPatientenNamen(): string {
        let text = '';
        if (this.currentGruppe?.unternehmensgruppe) {
            text += this.currentGruppe.unternehmensgruppe + ' ';
        }

        text += this.currentGruppe?.kunde;

        if (this.currentGruppe?.bezeichnung !== 'Standard') {
            text += ' ' + this.currentGruppe?.bezeichnung;
        }
        return text;
    }

    createHeaderRecord(): HeaderRecord {
        const hr = new HeaderRecord();
        hr.senderName = 'MERLIN';

        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const day = now.getDate();
        const hour = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();

        hr.datetime = [
            year,
            (month > 9 ? '' : '0') + month,
            (day > 9 ? '' : '0') + day,
            (hour > 9 ? '' : '0') + hour,
            (minutes > 9 ? '' : '0') + minutes,
            (seconds > 9 ? '' : '0') + seconds,
        ].join('');

        return hr;
    }

    goBack() {
        this.router.navigate(['../'], { relativeTo: this.route }).then();
    }

    getVerdachtString(s: string) {
        const arr: string[] = s.split('^');
        return arr[1] + ' ( ' + arr[0] + ' )';
    }

    getInfektionsartName(value: string) {
        const art = this.infektionsarten.find(i => i.kuerzel === value || i.bezeichnung === value);
        return art?.bezeichnung || value;
    }

    getTierartName(value: string) {
        const art = this.tierartenList.find(t => t.kuerzel === value || t.tierart === value);
        return art?.tierart || value;
    }

    /** Merlin erwartet das Tierart-Kürzel (AN3), nicht die Bezeichnung (Rind). */
    private tierartKuerzel(value: string): string {
        const art = this.tierartenList.find(t => t.kuerzel === value || t.tierart === value);
        return art?.kuerzel || value;
    }

    /** Merlin erwartet das Infektionsart-Kürzel (M), nicht die Bezeichnung. */
    private infektionsartKuerzel(value: string): string {
        const art = this.infektionsarten.find(i => i.kuerzel === value || i.bezeichnung === value);
        return art?.kuerzel || value;
    }

    public editIsolat(resTest: ResTest): void {
        this.currentIsolat = resTest;

        const verdacht = this.verdachte.find(v => v.verdacht === resTest.verdacht);
        this.currentVerdacht = verdacht;
        const tierart = this.tierartenList.find(t => t.kuerzel === resTest.tierart || t.tierart === resTest.tierart);
        this.tierartCtrl.setValue(tierart);
        const infenktionsart = this.infektionsarten.find(
            i => i.kuerzel === resTest.infektionsart || i.bezeichnung === resTest.infektionsart,
        );
        this.infektionCtrl.setValue(infenktionsart);
    }

    public deleteIsolate(index: number): void {
        this.resTestList.splice(index, 1);
        for (let i = 0; i < this.resTestList.length; i++) {
            this.resTestList[i].isolatenNummer = i + 1;
        }
    }

    drop(event: CdkDragDrop<ResTest[]>): void {
        if (!this.resTestList) {
            console.error('resTestList is null or undefined.');
            return;
        }

        if (event.previousIndex != null && event.currentIndex != null) {
            moveItemInArray(this.resTestList, event.previousIndex, event.currentIndex);
            for (let i = 0; i < this.resTestList.length; i++) {
                this.resTestList[i].isolatenNummer = i + 1;
            }
        } else {
            console.error('Invalid drag indices:', event.previousIndex, event.currentIndex);
        }
    }

    public resetFelder(): void {
        this.currentIsolat = new ResTest();
        this.currentIsolat.isolatenNummer = -1;
        this.currentVerdacht = undefined;
        this.verdachtAutocomplete.clear();
        this.tierartCtrl.setValue('');
        this.infektionCtrl.setValue('');
    }

    saveIsolat() {
        this.currentIsolat.tierart = this.tierartCtrl.value?.kuerzel;
        this.currentIsolat.infektionsart = this.infektionCtrl.value?.kuerzel;
        if (this.currentIsolat.isolatenNummer === -1) {
            this.currentIsolat.isolatenNummer = this.resTestList.length + 1;
            this.resTestList.push(this.currentIsolat);
        } else {
            this.resTestList[this.currentIsolat.isolatenNummer - 1] = this.currentIsolat;
        }
        this.resetFelder();
        this.saveAntibiogrammOrder(this.antibiogramm.status);
    }

    private ladeTierarten() {
        this.service.ladeTierarten().subscribe({
            next: response => {
                this.tierartenList = response.records;
            },
        });
    }

    tierartenComparator(t1: AntibiogrammTierart, t2: AntibiogrammTierart) {
        return t1?.tierart_id === t2?.tierart_id;
    }

    setCurrentVerdacht(verdacht: Verdacht) {
        if (verdacht) {
            console.log('verdacht: ', JSON.stringify(verdacht));
            this.currentVerdacht = verdacht;
            this.currentIsolat.verdacht = verdacht.kuerzel;
        }
    }

    private initRestTestList() {
        if (this.testDescription) {
            const testOrderResultRecords = this.testDescription?.testOrderResultRecords;
            for (const testOrderResultRecord of testOrderResultRecords) {
                const testOrderRecord = testOrderResultRecord.testOrderRecord;
                const resultRecord = testOrderResultRecord.resultRecord;
                const resTest = new ResTest();
                resTest.isolatenNummer = testOrderRecord.isolatenNummer;
                resTest.verdacht = resultRecord.verdacht;
                resTest.tierart = testOrderRecord.einsenderKuerzel;
                resTest.infektionsart = testOrderRecord.materialartenKuerzel;
                resTest.kennzeichnung = testOrderRecord.kennzeichnung;
                resTest.kommentar = testOrderRecord.isoKommentar;
                resTest.testart = resultRecord.testart;

                this.resTestList.push(resTest);
            }
        }
    }
}
